import { useState } from 'react'
import { X, AlertTriangle } from 'lucide-react'

// Local definitions to avoid shared package import issues
enum RejectionReason {
  TOO_EXPENSIVE = 'TOO_EXPENSIVE',
  WRONG_MOUNTING_SYSTEM = 'WRONG_MOUNTING_SYSTEM',
  MISSING_CERTIFICATIONS = 'MISSING_CERTIFICATIONS',
  DELIVERY_TOO_LONG = 'DELIVERY_TOO_LONG',
  INSUFFICIENT_WARRANTY = 'INSUFFICIENT_WARRANTY',
  TECHNICAL_SPECIFICATIONS = 'TECHNICAL_SPECIFICATIONS',
  VENDOR_REPUTATION = 'VENDOR_REPUTATION',
  PAYMENT_TERMS = 'PAYMENT_TERMS',
  LOCATION_ISSUES = 'LOCATION_ISSUES',
  OTHER = 'OTHER'
}

const REJECTION_REASON_LABELS: Record<RejectionReason, string> = {
  [RejectionReason.TOO_EXPENSIVE]: 'Too expensive',
  [RejectionReason.WRONG_MOUNTING_SYSTEM]: 'Wrong mounting system',
  [RejectionReason.MISSING_CERTIFICATIONS]: 'Missing certifications',
  [RejectionReason.DELIVERY_TOO_LONG]: 'Delivery time too long',
  [RejectionReason.INSUFFICIENT_WARRANTY]: 'Insufficient warranty terms',
  [RejectionReason.TECHNICAL_SPECIFICATIONS]: 'Technical specifications don\'t match',
  [RejectionReason.VENDOR_REPUTATION]: 'Vendor reputation concerns',
  [RejectionReason.PAYMENT_TERMS]: 'Unacceptable payment terms',
  [RejectionReason.LOCATION_ISSUES]: 'Location/logistics issues',
  [RejectionReason.OTHER]: 'Other (please specify)'
}

interface QuoteRejectionModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (reason: string, comment?: string) => void
  quoteName: string
  vendorName: string
  loading?: boolean
}

export default function QuoteRejectionModal({
  isOpen,
  onClose,
  onConfirm,
  quoteName,
  vendorName,
  loading = false
}: QuoteRejectionModalProps) {
  const [selectedReason, setSelectedReason] = useState<string>('')
  const [comment, setComment] = useState('')
  const [showCommentField, setShowCommentField] = useState(false)

  const handleReasonChange = (reason: string) => {
    setSelectedReason(reason)
    setShowCommentField(reason === RejectionReason.OTHER)
    if (reason !== RejectionReason.OTHER) {
      setComment('')
    }
  }

  const handleConfirm = () => {
    if (!selectedReason) return
    
    if (selectedReason === RejectionReason.OTHER && !comment.trim()) {
      return // Don't submit if OTHER is selected but no comment provided
    }
    
    onConfirm(selectedReason, comment.trim() || undefined)
  }

  const isValid = selectedReason && (selectedReason !== RejectionReason.OTHER || comment.trim())

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <AlertTriangle className="w-6 h-6 text-red-500 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Reject Quote</h3>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Quote Info */}
        <div className="mb-6 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600">
            <strong>Quote:</strong> {quoteName}
          </p>
          <p className="text-sm text-gray-600">
            <strong>Vendor:</strong> {vendorName}
          </p>
        </div>

        {/* Rejection Reason Dropdown */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reason for rejection <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedReason}
            onChange={(e) => handleReasonChange(e.target.value)}
            disabled={loading}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select a reason...</option>
            {Object.entries(REJECTION_REASON_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {/* Optional Comment Field */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional comments {showCommentField && <span className="text-red-500">*</span>}
            <span className="text-gray-500 font-normal">(optional)</span>
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={loading}
            placeholder={showCommentField ? "Please specify..." : "Any additional feedback for the vendor..."}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          />
          {showCommentField && !comment.trim() && (
            <p className="text-xs text-red-500 mt-1">
              Please specify the reason when selecting "Other"
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex space-x-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading || !isValid}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Rejecting...' : 'Reject Quote'}
          </button>
        </div>
      </div>
    </div>
  )
} 